import { useCallback, useRef, useState } from "react";
import { API } from "@/lib/api";

// Streams a chat response via fetch + ReadableStream (EventSource can't POST).
// Persisted mode: pass conv_id (or null to create new); backend persists both turns.
// Guest mode: pass guest=true; nothing is persisted server-side.
export function useChatStream() {
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);

  const send = useCallback(async ({ conv_id = null, message, model = null, guest = false, onMeta, onDelta, onDone, onError }) => {
    if (streaming) return;
    setStreaming(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ conv_id, message, model, guest }),
        signal: ctrl.signal,
      });
      if (!resp.ok) {
        onError?.(`HTTP ${resp.status}`);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let doneFired = false;
      const fireDone = () => { if (!doneFired) { doneFired = true; onDone?.(); } };
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!frame.startsWith("data:")) continue;
          const jsonStr = frame.slice(5).trim();
          try {
            const ev = JSON.parse(jsonStr);
            if (ev.type === "meta") onMeta?.(ev);
            else if (ev.type === "delta") onDelta?.(ev.content);
            else if (ev.type === "done") fireDone();
            else if (ev.type === "error") onError?.(ev.message);
          } catch { /* skip malformed */ }
        }
      }
      fireDone();
    } catch (e) {
      if (e.name !== "AbortError") onError?.(e.message);
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [streaming]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { streaming, send, cancel };
}
